import { useMemo, useState } from 'react';
import {
  ProfileSelectorFilterOptionKey,
  ProfileSelectorPageTypes,
} from 'app/components/profileSelector/types/profileSelectorFilter';
import { PROFILE_SELECTOR_FILTER_OPTIONS_PROPERTIES } from 'app/components/profileSelector/constants';
import getPageTypeFilterOptions from 'app/components/profileSelector/utils/getPageTypeFilterOptions';
import getFilteredDataBySearchQuery from 'app/components/profileSelector/utils/getFilteredDataBySearchQuery';
import { profileGroupsEligibleForAnalyticsPicker } from 'app/modules/analytics/utils/analyticsNonGlobalProfileGroups';
import { isAnalyticsEligiblePage } from 'app/modules/analytics/utils/analyticsEligibleProfileGroups';
import type { PageData, ProfileGroup } from 'types/Page';

const useAnalyticsSelectorFilter = (
  pages: PageData[],
  profileGroups: ProfileGroup[],
) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilterOption, setSelectedFilterOption] = useState<
    ProfileSelectorFilterOptionKey | ProfileSelectorPageTypes
  >(ProfileSelectorFilterOptionKey.All);

  const connectedPages = useMemo(
    () =>
      pages.filter((page) => isAnalyticsEligiblePage(page) && page.isConnected),
    [pages],
  );

  const selectableProfileGroups = useMemo(
    () => profileGroupsEligibleForAnalyticsPicker(profileGroups),
    [profileGroups],
  );

  const sidebarPageTypeOptions = useMemo(() => {
    return getPageTypeFilterOptions(
      connectedPages.map((page) => page.type as ProfileSelectorPageTypes),
    );
  }, [connectedPages]);

  const sidebarFilterOptions = useMemo(
    () => [
      {
        key: ProfileSelectorFilterOptionKey.All,
        ...PROFILE_SELECTOR_FILTER_OPTIONS_PROPERTIES[
          ProfileSelectorFilterOptionKey.All
        ],
      },
      {
        key: ProfileSelectorFilterOptionKey.ProfileGroups,
        ...PROFILE_SELECTOR_FILTER_OPTIONS_PROPERTIES[
          ProfileSelectorFilterOptionKey.ProfileGroups
        ],
      },
      ...sidebarPageTypeOptions,
    ],
    [sidebarPageTypeOptions],
  );

  const profilesByFilter = useMemo(() => {
    if (selectedFilterOption === ProfileSelectorFilterOptionKey.All) {
      return connectedPages;
    }

    if (selectedFilterOption === ProfileSelectorFilterOptionKey.ProfileGroups) {
      return [];
    }

    const numericFilter = Number(selectedFilterOption);
    if (!Number.isNaN(numericFilter)) {
      return connectedPages.filter((page) => page.type === numericFilter);
    }

    return [];
  }, [connectedPages, selectedFilterOption]);

  const groupsByFilter = useMemo(() => {
    if (
      selectedFilterOption === ProfileSelectorFilterOptionKey.All ||
      selectedFilterOption === ProfileSelectorFilterOptionKey.ProfileGroups
    ) {
      return selectableProfileGroups;
    }

    return [];
  }, [selectableProfileGroups, selectedFilterOption]);

  const filteredProfiles = useMemo(() => {
    return getFilteredDataBySearchQuery<PageData>(
      searchQuery,
      profilesByFilter,
    );
  }, [profilesByFilter, searchQuery]);

  const filteredProfileGroups = useMemo(() => {
    return getFilteredDataBySearchQuery<ProfileGroup>(
      searchQuery,
      groupsByFilter,
    );
  }, [groupsByFilter, searchQuery]);

  return {
    sidebarFilterOptions,
    searchQuery,
    setSearchQuery,
    selectedFilterOption,
    selectFilterOption: setSelectedFilterOption,
    filteredProfiles,
    filteredProfileGroups,
  };
};

export default useAnalyticsSelectorFilter;
